import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MouseEventHandler, useEffect } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';

import { updateCustomer } from '../../../apis/queries';
import SubmitButton from '../../../components/Buttons/SubmitButton';
import { isValidPhoneNumber } from '../../../helpers/validation';
import { Customer } from '../../../types';
import DisplayFieldErrors from '../../../utils/DisplayFormError';
import { formatDateTime } from '../../../utils/formatDateTime';
import { notification, NotificationKinds } from '../../../widgets/Notifications/notifications';

interface UpdateCustomerFormInput {
  name: string;
  phoneNumber: string;
  customerType: string;
  isActive: boolean;
}

const CustomerDetailForm = ({
  customer,
  isEditMode,
  onEditFormSubmit
}: {
  customer: Customer;
  isEditMode: boolean;
  isEditFormSubmitting: boolean;
  onEditFormSubmit: (value: boolean) => void;
}) => {
  const { name, phoneNumber, customerType, isActive, _id, createdAt, updatedAt } = customer;
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (data: UpdateCustomerFormInput) => {
      return updateCustomer(String(_id), {
        name: data.name,
        phoneNumber: data.phoneNumber,
        customerType: data.customerType,
        isActive: data.isActive
      });
    },
    onSuccess: () => {
      notification({
        variant: NotificationKinds.SUCCESS,
        title: `CUSTOMER UPDATED`,
        subtitle: `Customer has been updated successfully`
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['customers', { id: _id }] });
    }
  });

  const loading = mutation?.isPending;

  const createdBy = customer.createdBy.fullName ?? '';

  useEffect(() => {
    onEditFormSubmit(loading);
  }, [loading]);

  const methods = useForm<UpdateCustomerFormInput>({
    mode: 'onBlur',
    defaultValues: {
      name,
      phoneNumber,
      customerType,
      isActive
    }
  });

  const {
    register,
    formState: { isValid, errors, isDirty },
    reset,
    getValues
  } = methods;

  const onUpdateCustomer: MouseEventHandler<HTMLButtonElement> = event => {
    event.preventDefault();
    const fields = getValues();
    mutation?.mutate(fields);
  };

  const disabled = loading || !isEditMode;

  const handleCancel = () => {
    reset();
  };

  return (
    <Form>
      <Form.Group className="my-4">
        <Form.Check
          className="fw-bold"
          type="switch"
          label={'Activate Customer'}
          {...register('isActive', { disabled })}
        />
      </Form.Group>
      <Form.Group className="my-3">
        <Form.Label>Customer Name</Form.Label>
        <Form.Control
          type="text"
          placeholder="John Doe"
          {...register('name', {
            required: 'Name is required',
            disabled,
            minLength: { value: 3, message: 'Name must be atleast 3 characters' },
            validate: value => value.trim() !== '' || 'Nickname cannot be empty',
            pattern: {
              value: /^[a-zA-Z0-9]+( [a-zA-Z0-9]+)*$/,
              message: 'Full name should contain only alphabets and numbers'
            }
          })}
        />
        {errors?.name && <DisplayFieldErrors displayText={errors.name.message!} />}
      </Form.Group>
      <Form.Group className="my-3">
        <Form.Label>Phone Number</Form.Label>
        <Form.Control
          type="text"
          placeholder="08039283847"
          {...register('phoneNumber', {
            required: 'Phone number is required',
            disabled,
            pattern: {
              value: /^[0-9]+$/,
              message: 'Phone number must contain only digits'
            },
            validate: {
              isNotEmpty: value => value.trim() !== '' || 'Phone number cannot be empty',
              isValidFormat: isValidPhoneNumber
            }
          })}
        />
        {errors?.phoneNumber && <DisplayFieldErrors displayText={errors.phoneNumber.message!} />}
      </Form.Group>
      <Form.Group className="my-3">
        <Form.Label htmlFor="customerType">Customer Type</Form.Label>
        <Form.Select
          id="customerType"
          {...register('customerType', {
            required: 'Customer type is required',
            disabled
          })}
        >
          <option value="Delivery">Delivery</option>
          <option value="MDP">MDP</option>
        </Form.Select>
        {errors?.customerType && <DisplayFieldErrors displayText={errors.customerType.message!} />}
      </Form.Group>
      <Form.Group className="my-3">
        <Form.Label>Created By</Form.Label>
        <Form.Control type="text" placeholder="Created By" disabled={true} value={createdBy.toString()} />
      </Form.Group>
      <Form.Group className="my-3">
        <Form.Label>Created At</Form.Label>
        <Form.Control type="text" placeholder="Created At" disabled={true} value={formatDateTime(createdAt)} />
      </Form.Group>
      <Form.Group className="my-3">
        <Form.Label>Last Updated</Form.Label>
        <Form.Control type="text" placeholder="Updated At" disabled={true} value={formatDateTime(updatedAt)} />
      </Form.Group>

      {isEditMode && (
        <Row className="my-5">
          <Col>
            <SubmitButton
              type="button"
              buttonText="cancel"
              variant="white"
              onClick={handleCancel}
              disabled={disabled}
              loading={false}
            />
          </Col>
          <Col>
            <SubmitButton
              type="submit"
              buttonText="Submit"
              onClick={onUpdateCustomer}
              loading={loading}
              disabled={!isDirty || !isValid || disabled}
            />
          </Col>
        </Row>
      )}
    </Form>
  );
};

export default CustomerDetailForm;
